import { type FC, useEffect, useMemo, useState } from 'react'

import 'swiper/css'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import type { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import * as SvgIcons from 'assets/svg/sprite'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { PreviewContext, usePreviewContext } from 'context/previewContext'
import PreviewComments from 'features/Preview/PreviewComments/PreviewComments'
import SinglePostPreview from 'features/Preview/SinglePostPreview/SinglePostPreview'
import { useFilterQuery } from 'helpers/hooks'
import { getComment } from 'services/endpoints/post'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import { type IComment, type IPost } from 'types/interfaces/ITypes'

import styles from './_mediaHomeModal.module.scss'

type MediaHomeModalProps = {
  closeFn: () => void
  newPostPage?: boolean
  feed: IPost[]
  initialIndex: number
}

const MediaHomeModal: FC<MediaHomeModalProps> = ({
  closeFn,
  newPostPage,
  feed,
  initialIndex
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const modalContext = useModalContext()

  const [singleComment, setSingleComment] = useState<IComment>()
  const [hasControls, setHasControls] = useState(true)
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)

  const searchParams = useFilterQuery()
  const userData = useUserProfile()
  const baseCtx = usePreviewContext()

  // IMPORTANT: subscribe directly to the store for reactive updates
  const commentsActive = usePreviewStore(s => s.commentsActive)

  if (!Array.isArray(feed) || feed.length === 0) {
    return null
  }

  const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(n, max))

  const start = clamp(initialIndex ?? 0, 0, feed.length - 1)
  const [activeIndex, setActiveIndex] = useState<number>(start)

  const activePost = feed[activeIndex] ?? null
  const activePostId = activePost?.id
  const isOwner = !!activePost && userData?.id === activePost?.user?.id

  useEffect(() => {
    const commentId = searchParams.get('commentId')

    if (commentId) {
      getComment(commentId).then(resp => {
        setSingleComment(resp.data)
        baseCtx.setCommentsActive(true)
      })
    } else {
      setSingleComment(undefined)
    }
  }, [searchParams, baseCtx])

  useEffect(() => {
    return () => baseCtx.resetPreviewState()
  }, [baseCtx])

  const makeCtxForPost = () => ({
    ...baseCtx,
    selectedSlide: 0,
    userId: userData?.id ?? null,
    filter: 'all',
    query: ['media-home-feed'],
    previewLocation: 'media-home'
  })

  useEffect(() => {
    const initialPost = feed[start]
    if (initialPost) {
      baseCtx.setSelectedPostData(initialPost)
    }
  }, [feed, start, baseCtx])

  const openOptions = () => {
    setIsOptionsNavOpen(true)
  }

  const viewerOptions = useMemo(() => {
    if (!activePostId) return []

    const itemType =
      activePost?.type?.toLowerCase() === 'message'
        ? 'message'
        : 'post'

    return [
      {
        icon: <SvgIcons.IconOptionsPlaylist />,
        text: t('addToList'),
        disabled: false,
        action: () => {
          setIsOptionsNavOpen(false)

          modalContext.addModal(
            '',
            <AddToPlaylist
              itemId={activePostId}
              itemType={itemType}
              closeFn={() => {
                modalContext.clearModal()
              }}
            />,
            true,
            true
          )
        }
      }
    ]
  }, [
    activePostId,
    activePost?.type,
    t,
    modalContext
  ])

  return (
    <div className={styles.postContainer}>
      {!!activePostId && isOptionsNavOpen && (
        <OptionsDropdown
          role={isOwner ? 'owner' : 'viewer'}
          content={
            activePost?.type?.toLowerCase() === 'message'
              ? 'message'
              : 'post'
          }
          isOptionsNavOpen={isOptionsNavOpen}
          setIsOptionsNavOpen={setIsOptionsNavOpen}
          postId={activePostId}
          options={viewerOptions}
          deleteFn={() => {
            setIsOptionsNavOpen(false)
          }}
          withoutBottomNavigation
        />
      )}

      <Swiper
        direction="vertical"
        initialSlide={start}
        onSlideChange={(sw: SwiperType) => {
          const nextIndex = sw.activeIndex
          const nextPost = feed[nextIndex]

          setActiveIndex(nextIndex)
          setIsOptionsNavOpen(false)

          if (commentsActive) {
            baseCtx.setCommentsActive(false)
          }

          if (nextPost) {
            baseCtx.setSelectedPostData(nextPost)
          }
        }}
        slidesPerView={1}
        style={{ height: '100vh' }}
        speed={250}
      >
        {feed.map((p, i) => (
          <SwiperSlide key={`media-feed-${p.id}-${i}`}>
            <PreviewContext.Provider value={makeCtxForPost()}>
              <SinglePostPreview
                post={p}
                showControls={hasControls}
                isFirstPost={false}
                hasNextPage={i < feed.length - 1}
                isInViewport={i === activeIndex}
                minimizeFn={() => {}}
                toggleControls={() => setHasControls(v => !v)}
                newPostPage={newPostPage}
                onClose={closeFn}
                onOpenOptions={openOptions}
                setIsOptionsNavOpen={setIsOptionsNavOpen}
                independentPreviewVisibility
                
              />
            </PreviewContext.Provider>
          </SwiperSlide>
        ))}
      </Swiper>

      {activePost && commentsActive && (
        <CommentsLayer>
          {singleComment ? (
            <PreviewComments
              commentsActive={commentsActive}
              setCommentsActive={baseCtx.setCommentsActive}
              activeCommentsPostId={activePost.id}
              commentCount={activePost?.comment_count ?? 0}
              isMyPost={userData?.id === activePost?.user?.id}
              isSingleCommentLink={!!singleComment}
              viewAllCommentsFn={() => {
                navigate({ search: undefined })
              }}
            />
          ) : (
            <PreviewComments
              commentsActive={commentsActive}
              setCommentsActive={baseCtx.setCommentsActive}
              activeCommentsPostId={activePost.id}
              commentCount={activePost?.comment_count ?? 0}
              isMyPost={userData?.id === activePost?.user?.id}
            />
          )}
        </CommentsLayer>
      )}
    </div>
  )
}

export default MediaHomeModal

const CommentsLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
`